import React, { useState, useEffect, useRef, useCallback } from 'react'
import { gql } from 'graphql-request'
import { authorizedGraphQLRequest } from '@/api/graphqlClient'
import { Search, ArrowLeft, Star, Check, Target, ChevronRight } from 'lucide-react'
import { Button } from '@/components/ui/button'
import UserReviewList from './UserReviewList'
import type { OrganisationWithDetails } from '../index'

interface UserTargetsTabProps {
  organisation: OrganisationWithDetails | null
  loading: boolean
}

interface UserTargetDetail {
  rolePlayId: number
  rolePlayName: string
  sessionsPlayed: number
  targetSessions: number
  totalScore: number
  targetScore: number
}

interface UserTargetStats {
  userId: number
  email: string
  firstName: string | null
  lastName: string | null
  totalSessions: number
  avgScore: number
  targetsCount: number
  sessionsMetCount: number
  scoreMetCount: number
  targets: UserTargetDetail[]
}

const GET_ORGANISATION_USER_TARGET_STATS = gql`
  query getOrganisationUserTargetStats($organisationId: Int!, $limit: Int, $offset: Int, $search: String) {
    getOrganisationUserTargetStats(organisationId: $organisationId, limit: $limit, offset: $offset, search: $search) {
      userId
      email
      firstName
      lastName
      totalSessions
      avgScore
      targetsCount
      sessionsMetCount
      scoreMetCount
      targets {
        rolePlayId
        rolePlayName
        sessionsPlayed
        targetSessions
        totalScore
        targetScore
      }
    }
  }
`

const PAGE_SIZE = 10

const StarRating: React.FC<{ score: number }> = ({ score }) => {
  const fullStars = Math.floor(score)
  const hasHalf = score - fullStars >= 0.5
  const emptyStars = 5 - fullStars - (hasHalf ? 1 : 0)

  return (
    <div className="flex items-center gap-0.5">
      {Array.from({ length: fullStars }).map((_, i) => (
        <Star key={`full-${i}`} size={14} className="fill-yellow-400 text-yellow-400" />
      ))}
      {hasHalf && <Star key="half" size={14} className="fill-yellow-400/50 text-yellow-400" />}
      {Array.from({ length: emptyStars }).map((_, i) => (
        <Star key={`empty-${i}`} size={14} className="text-gray-300" />
      ))}
      <span className="ml-1 text-xs text-gray-500">{score.toFixed(1)}</span>
    </div>
  )
}

const UserTargetsTab: React.FC<UserTargetsTabProps> = ({ organisation, loading }) => {
  const [users, setUsers] = useState<UserTargetStats[]>([])
  const [loadingStats, setLoadingStats] = useState(false)
  const [offset, setOffset] = useState(0)
  const [hasMore, setHasMore] = useState(true)
  const [search, setSearch] = useState('')
  const [selectedUser, setSelectedUser] = useState<UserTargetStats | null>(null)
  // Level 3: one of the selected user's role plays, whose sessions are listed.
  const [selectedTarget, setSelectedTarget] = useState<UserTargetDetail | null>(null)
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const fetchStats = useCallback(async (orgId: number, searchValue: string, currentOffset: number, append: boolean) => {
    try {
      setLoadingStats(true)
      const response = await authorizedGraphQLRequest<{
        getOrganisationUserTargetStats: UserTargetStats[]
      }>(GET_ORGANISATION_USER_TARGET_STATS, {
        organisationId: orgId,
        limit: PAGE_SIZE,
        offset: currentOffset,
        search: searchValue || undefined,
      })
      const results = response.getOrganisationUserTargetStats || []
      setUsers((prev) => (append ? [...prev, ...results] : results))
      setHasMore(results.length === PAGE_SIZE)
    } catch (error) {
      console.error('Failed to fetch user target stats:', error)
    } finally {
      setLoadingStats(false)
    }
  }, [])

  // Reset and fetch when organisation changes
  useEffect(() => {
    if (organisation?.id) {
      setOffset(0)
      setSearch('')
      setUsers([])
      setHasMore(true)
      setSelectedUser(null)
      setSelectedTarget(null)
      fetchStats(organisation.id, '', 0, false)
    }
  }, [organisation?.id, fetchStats])

  const handleSearchChange = (value: string) => {
    setSearch(value)

    if (debounceRef.current) {
      clearTimeout(debounceRef.current)
    }

    debounceRef.current = setTimeout(() => {
      if (!organisation?.id) return

      if (value.length === 0) {
        setOffset(0)
        setUsers([])
        setHasMore(true)
        fetchStats(organisation.id, '', 0, false)
      } else if (value.length >= 3) {
        setOffset(0)
        setUsers([])
        setHasMore(true)
        fetchStats(organisation.id, value, 0, false)
      }
    }, 500)
  }

  const handleLoadMore = () => {
    if (!organisation?.id || loadingStats) return
    const newOffset = offset + PAGE_SIZE
    setOffset(newOffset)
    fetchStats(organisation.id, search, newOffset, true)
  }

  const handleUserClick = (user: UserTargetStats) => {
    setSelectedUser(user)
  }

  const handleBack = () => {
    setSelectedUser(null)
    setSelectedTarget(null)
  }

  const handleTargetClick = (target: UserTargetDetail) => {
    if (target.sessionsPlayed > 0) setSelectedTarget(target)
  }

  const handleBackToTargets = () => {
    setSelectedTarget(null)
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    )
  }

  if (!organisation) {
    return <div className="text-center text-gray-500 py-12">No organisation selected</div>
  }

  // Level 3: the selected user's sessions of one role play, with their feedback
  if (selectedUser && selectedTarget) {
    return (
      <div className="space-y-6">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="sm" onClick={handleBackToTargets}>
            <ArrowLeft size={16} className="mr-1" />
            Back
          </Button>
          <div className="min-w-0">
            <h3 className="text-lg font-semibold text-gray-900 truncate">{selectedTarget.rolePlayName}</h3>
            <p className="text-sm text-gray-500 truncate">{selectedUser.email}</p>
          </div>
        </div>

        <UserReviewList organisationId={organisation.id} userId={selectedUser.userId} rolePlayId={selectedTarget.rolePlayId} />
      </div>
    )
  }

  // Level 2: User detail with target cards
  if (selectedUser) {
    const avgStars =
      selectedUser.targets.length > 0
        ? selectedUser.targets.reduce((sum, t) => {
            if (t.sessionsPlayed > 0) return sum + t.totalScore / t.sessionsPlayed
            return sum
          }, 0) / selectedUser.targets.filter((t) => t.sessionsPlayed > 0).length || 0
        : 0

    return (
      <div className="space-y-6">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="sm" onClick={handleBack}>
            <ArrowLeft size={16} className="mr-1" />
            Back
          </Button>
          <h3 className="text-lg font-semibold text-gray-900">{selectedUser.email}</h3>
        </div>

        {selectedUser.targets.length === 0 ? (
          <div className="text-center text-gray-500 py-8 bg-gray-50 rounded-xl">
            <Target className="h-12 w-12 mx-auto mb-3 text-gray-300" />
            <p>No targets configured for this organisation</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {selectedUser.targets.map((target) => {
              const avgStarScore = target.sessionsPlayed > 0 ? target.totalScore / target.sessionsPlayed : 0
              const avgScoreScaled = avgStarScore * 20
              const sessionsMet = target.sessionsPlayed >= target.targetSessions
              const scoreMet = target.sessionsPlayed > 0 && avgScoreScaled >= target.targetScore
              // A card with sessions opens their feedback; one without has nothing to show.
              const hasSessions = target.sessionsPlayed > 0

              return (
                <div
                  key={target.rolePlayId}
                  className={`bg-white border border-gray-200 rounded-xl p-5 space-y-3 ${hasSessions ? 'cursor-pointer hover:border-primary/40 hover:shadow-md transition-all focus:outline-none focus:ring-2 focus:ring-primary/20' : ''}`}
                  role={hasSessions ? 'button' : undefined}
                  tabIndex={hasSessions ? 0 : undefined}
                  aria-label={hasSessions ? `View sessions of ${target.rolePlayName}` : undefined}
                  onClick={hasSessions ? () => handleTargetClick(target) : undefined}
                  onKeyDown={
                    hasSessions
                      ? (e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault()
                            handleTargetClick(target)
                          }
                        }
                      : undefined
                  }
                >
                  <div className="flex items-start justify-between gap-2">
                    <h4 className="font-medium text-gray-900 text-sm">{target.rolePlayName}</h4>
                    {hasSessions && (
                      <span className="inline-flex items-center gap-0.5 text-[11px] text-primary whitespace-nowrap">
                        View sessions
                        <ChevronRight size={12} />
                      </span>
                    )}
                  </div>

                  <div className="flex items-center justify-between">
                    <span className="text-xs text-gray-500">Sessions</span>
                    <div className="flex items-center gap-1.5">
                      <span className="text-sm font-medium">
                        {target.sessionsPlayed}/{target.targetSessions}
                      </span>
                      {sessionsMet && <Check size={14} className="text-green-500" />}
                    </div>
                  </div>

                  <div className="flex items-center justify-between">
                    <span className="text-xs text-gray-500">Avg Score</span>
                    <div className="flex items-center gap-1.5">
                      <StarRating score={avgStarScore} />
                      {scoreMet && <Check size={14} className="text-green-500" />}
                    </div>
                  </div>
                </div>
              )
            })}
          </div>
        )}
      </div>
    )
  }

  // Level 1: User list
  return (
    <div className="space-y-6">
      {/* Search */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
        <input
          type="text"
          value={search}
          onChange={(e) => handleSearchChange(e.target.value)}
          placeholder="Search by email"
          className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
        />
        {search.length > 0 && search.length < 3 && <p className="text-xs text-gray-400 mt-1">Type at least 3 characters to search</p>}
      </div>

      {/* User List */}
      {loadingStats && users.length === 0 ? (
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
        </div>
      ) : users.length === 0 ? (
        <div className="text-center text-gray-500 py-8 bg-gray-50 rounded-xl">
          <Target className="h-12 w-12 mx-auto mb-3 text-gray-300" />
          <p>No users found. Make sure targets are configured for this organisation.</p>
        </div>
      ) : (
        <>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">User</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Sessions</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Avg Score</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Sessions Met</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Score Met</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {users.map((user) => (
                  <tr key={user.userId} onClick={() => handleUserClick(user)} className="hover:bg-gray-50 cursor-pointer">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="text-sm font-medium text-gray-900">{user.email}</span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{user.totalSessions}</td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <StarRating score={user.avgScore} />
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {user.sessionsMetCount}/{user.targetsCount}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {user.scoreMetCount}/{user.targetsCount}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* View More */}
          {hasMore && (
            <div className="flex justify-center">
              <Button variant="outline" onClick={handleLoadMore} disabled={loadingStats}>
                {loadingStats ? 'Loading...' : 'View More'}
              </Button>
            </div>
          )}
        </>
      )}
    </div>
  )
}

export default UserTargetsTab
